// NewWorkflowSection — body-only (dvs. utan <Dialog>-wrapper) variant
// av NewWorkflowDialog:s type-chooser. Förberedande extract för #9
// WorkspacePickerDialog-merge (POST_QA_SUBPLAN Wave E.2 step 1).
//
// Användning:
//   - Standalone-dialog-läge: rendreras av NewWorkflowDialog (legacy
//     wrapper, fortfarande exporterad så call-sites inte bryts).
//   - Tabs-läge: rendreras av den blivande WorkspacePickerDialog som
//     en av sina flikar.
//
// Verbatim-extract: KINDS-listan, lock-logik, knapp-render — identisk
// med originalet. Footer-knapparna (Avbryt / Skapa) embeddas via
// `footerSlot`-prop så den outer-dialogen / tabs-shellet kan välja
// var de placeras.

import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { FileText, Users, Rocket } from 'lucide-react';

export type WorkflowKind = 'standard' | 'coop' | 'process';

interface NewWorkflowSectionProps {
  /** Triggar canvas-rens + setCurrentDocumentId(null). */
  onConfirm: (kind: WorkflowKind) => void;
  /** Stänger värd-dialogen (vad det än är) — t.ex. via onOpenChange(false). */
  onCancel: () => void;
  /** Sant om user är på Enterprise-tier. */
  canRunProcesses: boolean;
  /** Sant om user:s plan tillåter co-op (Advanced+). */
  canUseCoop: boolean;
  /** Optional render-slot där värd-dialogen kan injicera sitt eget
   *  footer-element (t.ex. <DialogFooter>). Defaultar till en
   *  vanlig div när inget passas in. */
  footerSlot?: (footerContent: ReactNode) => ReactNode;
}

const KINDS: Array<{
  id: WorkflowKind;
  icon: typeof FileText;
  label: string;
  desc: string;
  hint: string;
}> = [
  {
    id: 'standard',
    icon: FileText,
    label: 'Standard',
    desc: 'Vanligt arbetsflöde. Kan delas eller köras som process senare.',
    hint: 'Spara, dela eller exportera som JSON när du är klar.',
  },
  {
    id: 'coop',
    icon: Users,
    label: 'Co-op (samarbete)',
    desc: 'Designat för flerpersons-redigering med live-sync.',
    hint: 'Spara först, klicka sedan "Dela arbetsyta" för att bjuda in editors.',
  },
  {
    id: 'process',
    icon: Rocket,
    label: 'Process-motor',
    desc: 'Körbart workflow — kan triggas via API, schema eller webhook.',
    hint: 'Kräver Enterprise-plan. Process-knappen aktiveras efter du sparat flödet.',
  },
];

export function NewWorkflowSection({
  onConfirm, onCancel, canRunProcesses, canUseCoop, footerSlot,
}: NewWorkflowSectionProps) {
  const [selected, setSelected] = useState<WorkflowKind>('standard');

  const handleConfirm = () => {
    onConfirm(selected);
    onCancel();
    setSelected('standard');
  };

  const isLocked = (id: WorkflowKind) =>
    (id === 'process' && !canRunProcesses) || (id === 'coop' && !canUseCoop);

  const footerContent = (
    <>
      <Button variant="outline" onClick={onCancel}>
        Avbryt
      </Button>
      <Button onClick={handleConfirm} data-testid="new-workflow-confirm">
        Skapa
      </Button>
    </>
  );

  return (
    <>
      <div className="space-y-2 py-2">
        {KINDS.map((kind) => {
          const locked = isLocked(kind.id);
          const Icon = kind.icon;
          const active = selected === kind.id;
          return (
            <button
              key={kind.id}
              type="button"
              onClick={() => !locked && setSelected(kind.id)}
              disabled={locked}
              className={`w-full text-left p-3 rounded-md border transition-colors ${
                active
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                  : 'border-border hover:bg-muted/50'
              } ${locked ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid={`new-workflow-kind-${kind.id}`}
            >
              <div className="flex items-start gap-3">
                <Icon className="w-5 h-5 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{kind.label}</span>
                    {locked && (
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground border rounded px-1">
                        {kind.id === 'process' ? 'Enterprise' : 'Advanced+'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{kind.desc}</p>
                  <p className="text-[11px] text-muted-foreground/80 mt-1 italic">{kind.hint}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {footerSlot ? footerSlot(footerContent) : (
        <div className="flex justify-end gap-2 pt-2">{footerContent}</div>
      )}
    </>
  );
}
